import { useState } from 'react'
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  Divider,
} from '@mui/material'
import { Settings, Check, NotificationsOff, Notifications, OpenInBrowser, SettingsSuggest, Visibility, VisibilityOff } from '@mui/icons-material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { chatApi } from '@api/endpoints/chat'
import { resolveNotificationMode, type NotificationMode } from '@api/types/chat'
import {
  useAutoDisplayForConversation,
  useHasConversationOverride,
  useImageDisplayPrefStore,
} from '@store/imageDisplayPrefStore'

/** Follow the global default, or give this one chat its own answer. */
const IMAGE_CHOICES: { value: boolean | null; label: string; description: string; icon: React.ReactNode }[] = [
  { value: null, label: 'Use default', description: '', icon: <SettingsSuggest fontSize="small" /> },
  { value: true, label: 'Show automatically', description: 'Images open as they arrive', icon: <Visibility fontSize="small" /> },
  { value: false, label: 'Tap to show', description: 'Images wait for a tap in this chat', icon: <VisibilityOff fontSize="small" /> },
]

interface ConversationSettingsMenuProps {
  conversationId: string
  currentMode?: NotificationMode | number | null
  /** Color for the gear icon (e.g. 'inherit' on a colored header). */
  color?: string
  size?: 'small' | 'medium'
}

const MODES: Array<{ value: NotificationMode; label: string; description: string; icon: React.ReactNode }> = [
  { value: 'Muted', label: 'Muted', description: 'No notifications', icon: <NotificationsOff fontSize="small" /> },
  { value: 'NoticeOnly', label: 'Notifications', description: 'Badge + toast', icon: <Notifications fontSize="small" /> },
  { value: 'AutoOpen', label: 'Auto-open', description: 'Pop the chat open', icon: <OpenInBrowser fontSize="small" /> },
]

/**
 * Gear menu for per-conversation settings. Currently exposes the notification mode (Muted /
 * Notifications / Auto-open); the gear is a deliberate home for future per-conversation settings.
 */
export function ConversationSettingsMenu({
  conversationId,
  currentMode,
  color = 'inherit',
  size = 'small',
}: ConversationSettingsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const queryClient = useQueryClient()
  const [pendingMode, setPendingMode] = useState<NotificationMode | null>(null)

  const mutation = useMutation({
    mutationFn: (mode: NotificationMode) =>
      chatApi.updateConversationSettings(conversationId, { notificationMode: mode }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
      queryClient.invalidateQueries({ queryKey: ['chatConversation', conversationId] })
    },
    onSettled: () => setPendingMode(null),
  })

  const active = pendingMode ?? resolveNotificationMode(currentMode)

  const autoDisplayDefault = useImageDisplayPrefStore((s) => s.autoDisplayChatImages)
  const setConversationOverride = useImageDisplayPrefStore((s) => s.setConversationOverride)
  const hasOverride = useHasConversationOverride(conversationId)
  const overrideValue = useAutoDisplayForConversation(conversationId)
  // null means it follows the default, which is a different state from happening to match it.
  const imageChoice: boolean | null = hasOverride ? overrideValue : null

  return (
    <>
      <Tooltip title="Settings">
        <IconButton
          size={size}
          onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget) }}
          sx={{ color, p: 0.5 }}
          aria-label="Conversation settings"
        >
          <Settings sx={{ fontSize: size === 'small' ? 16 : 20 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={(e) => { (e as React.MouseEvent).stopPropagation?.(); setAnchorEl(null) }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>
          Notifications
        </Typography>
        {MODES.map((m) => (
          <MenuItem
            key={m.value}
            selected={active === m.value}
            disabled={mutation.isPending}
            onClick={() => { setPendingMode(m.value); mutation.mutate(m.value); setAnchorEl(null) }}
          >
            <ListItemIcon>{m.icon}</ListItemIcon>
            <ListItemText primary={m.label} secondary={m.description} />
            {active === m.value && <Check fontSize="small" color="primary" sx={{ ml: 1 }} />}
          </MenuItem>
        ))}

        <Divider />

        {/* One chat is usually the reason somebody wants images to stop opening by themselves,
            and switching them off everywhere to deal with one person is the wrong shape of
            answer. Three states rather than a switch, because "follow the default" has to be
            reachable again — a chat that silently kept its own answer after the default changed
            would look like the default was being ignored. */}
        <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>
          Images
        </Typography>
        {IMAGE_CHOICES.map((choice) => (
          <MenuItem
            key={String(choice.value)}
            selected={imageChoice === choice.value}
            onClick={() => { setConversationOverride(conversationId, choice.value); setAnchorEl(null) }}
          >
            <ListItemIcon>{choice.icon}</ListItemIcon>
            <ListItemText
              primary={choice.label}
              secondary={
                choice.value === null
                  ? `Currently ${autoDisplayDefault ? 'on' : 'off'} for all chats`
                  : choice.description
              }
            />
            {imageChoice === choice.value && <Check fontSize="small" color="primary" sx={{ ml: 1 }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
